import { createContext, use } from "react";
import type { Call } from "../calls/call-state";
import type { CallSession } from "../calls/call-session";
import type { CallMedia, Person } from "../../shared/types";
import type { AccountRecovery } from "../account/account-recovery";
import type { GroupCallHolder } from "../group-calls/use-group-call";
import type { Screen } from "./location";

// A person to request after joining, with the profile link code that reaches
// them, or "link" to create a call link.
export type JoinTarget = { person: Person; code?: string } | "link";
export type SafetyAction = "block" | "remove-friend";

// The one dialog over the page. Opening another replaces it, and steps within
// a flow belong to that flow.
export type Dialog =
  | { kind: "enter"; step: "join" | "restore"; target?: JoinTarget }
  | { kind: "keep" }
  // A profile link's code lets a request reach someone username search does
  // not; a search term keeps the person shown current with its results.
  | { kind: "person"; person: Person; code?: string; search?: string }
  | { kind: "requests" }
  | { kind: "safety"; person: Person; action: SafetyAction }
  | { kind: "add-friend"; step: "add" | "share" }
  | { kind: "settings" }
  | { kind: "sounds" }
  | { kind: "sign-in" }
  | { kind: "edit" }
  | { kind: "avatar" }
  | { kind: "logout" }
  | { kind: "group-friends" };

// What the app's shell does for the flows and screens inside it.
export type Shell = {
  dialog: Dialog | null;
  open: (dialog: Dialog) => void;
  close: () => void;
  report: (person: Person) => void;
  // A dialog or call is over the page, so what animates behind it can rest.
  covered: boolean;
  openScreen: (view: Screen) => void;
  showChat: (person: Person) => void;
  openPerson: (person: Person) => void;
  chooseSafety: (person: Person, action: SafetyAction | "report") => void;
  liveCall: Call | null;
  session: CallSession;
  groupCall: GroupCallHolder;
  callFriend: (person: Person, media?: CallMedia) => void;
  // Joining waits for the signaling socket, which a new guest opens first.
  joinCall: (person: Person, media: CallMedia) => void;
  joining: boolean;
  startMicrophoneCheck: () => void;
  connected: boolean;
  createCallLink: () => Promise<void>;
  cancelCallLink: () => Promise<void>;
  recovery: AccountRecovery;
};

export const ShellContext = createContext<Shell | null>(null);

export function useShell() {
  const shell = use(ShellContext);
  if (!shell) throw new Error("useShell needs the app shell");
  return shell;
}
